import { useQuery } from "@tanstack/react-query";
import { useCreate, useDelete, useGetAll, useGetOne, useUpdate } from "./crudService";
import { format, isAfter, isBefore, isWithinInterval, parseISO } from "date-fns";
import { formatIso } from "@utils/dates";

const endpoint:string = 'businesses';

export function useGetAllBusinesses(params:{page:number}, queryParams:any = {}) {
    return useGetAll({queryKey: [endpoint], ...queryParams}, params);
}

export function useGetBusiness(id: number, params:any = {}) {
    return useGetOne({
        queryKey: [endpoint, id],
        transform: (data:any) => {
            return ensureHoursRelationship(data);
        },
        ...params
    });
}

export function useCreateBusiness() {
    return useCreate({queryKey: [endpoint]});
}

export function useUpdateBusiness({id}) {
    return useUpdate({queryKey: [endpoint, id]});
}

export function useDeleteBusiness({id}) {
    return useDelete({queryKey: [endpoint, id]});
}

/*
|--------------------------------------------
| Logo
|--------------------------------------------
*/
export function getBusinessLogo($business) {
    let $logo = null;

    for (let file in $business.files) {
        if( $business.files[file].type == 'logo' )
        {
            if (!$logo || $logo.created_at < $business.files[file].created_at) {
                $logo =  $business.files[file];
            }
        }
    }

    return $logo;
};


/*
|--------------------------------------------
| Hours
|--------------------------------------------
*/
export function ensureHoursRelationship(business:any) {
    if(business?.hours && business.hours.length > 0) {
        return business;
    }

    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    let initialHours = [];
    for(const day of daysOfWeek) {
        initialHours.push({
            day,
            open_time: null,// new Date(),
            close_time: null,// new Date(),
            is_closed: 1,
        });
    }

    business.hours = initialHours;

    return business;
};

export function getHoursForDay(hours, checkDay:string|boolean = false, basedOnNow = false) {
    const today = new Date();
    checkDay = checkDay || format(today, 'EEEE');
    basedOnNow = basedOnNow || false;

    var theDay:any = false;

    // Hours could be an array (of days) or an object (one single day)
    if(Object.prototype.toString.call( hours ) === '[object Array]')
    {
        for(let day in hours) {
            if(hours[day].day == checkDay)  {
                theDay = hours[day];
                break;
            }
        }
    }
    else
    {
        theDay = hours;
    }

    // No hours in the database
    if(theDay === false) {
        return 'Unknown';
    }

    // Do we know it's closed?
    if(theDay.is_closed)
    {
        return 'Closed Today';
    }

    if(theDay.open_time === null) {
        return 'By Appointment';
    }

    /*
    if(!theDay.open_time && theDay.close_time === '00:00:00') {
        return 'n/a';
    }
    */

    // Get the hours
    var open_time_today = theDay.open_time ? parseISO(formatIso(today) + " " + theDay.open_time) : today;
    var close_time_today = parseISO(formatIso(today) + " " + theDay.close_time);

    var hoursString = '';

    if (open_time_today) {
        // (if minutes is 00, only display hours)
        hoursString += parseInt(format(open_time_today, 'mm')) == 0 ? format(open_time_today, 'haaa') : format(open_time_today, 'h:mmaaa');
        hoursString += " – ";
        hoursString += parseInt(format(close_time_today, 'mm')) == 0 ? format(close_time_today, 'haaa') : format(close_time_today, 'h:mmaaa');
    } else {
        hoursString += "By Appointment";
    }


    // closed right now
    let closedNow = false;
    if (basedOnNow) {
        // if close time is before open time, the business is open past midnight so only compare the open date
        if(isBefore(close_time_today, open_time_today)) {
            if(isBefore(today, open_time_today)) {
                closedNow = true;
            }
        } else {
            // if it's before or after opening/closing time, it's now closed
            if(isBefore(today, open_time_today) || isAfter(today, close_time_today)) {
                closedNow = true;
            }
        }
    }

    if(closedNow) {
        hoursString = 'Closed ('+hoursString+')';
    }

    return hoursString;
};

export function isBusinessOpen(hours) {
    return getHoursForDay(hours, false, true);
};

/*
|--------------------------------------------
| Primary Property
|--------------------------------------------
*/
export function primaryProperty(properties:any[]) {
    let $primaryProperty = properties[0];

    for (let property in properties) {
        if( properties[property].pivot.is_primary )
        {
            $primaryProperty = properties[property];
        }
    }

    return $primaryProperty;
};

/*
|--------------------------------------------
| Owner
|--------------------------------------------
*/
/*
resource.owner = function(contacts) {
    return mm.helpers.contacts.filterByRole(contacts, 'owner');
};
*/

function orderHours(hours) {
    // return hours;
    let $ordered_days = {
        'Sunday': {
            'day': 'Sunday',
            'is_closed': 1
        },
        'Monday': {
            'day': 'Monday',
            'is_closed': 1
        },
        'Tuesday': {
            'day': 'Tuesday',
            'is_closed': 1
        },
        'Wednesday': {
            'day': 'Wednesday',
            'is_closed': 1
        },
        'Thursday': {
            'day': 'Thursday',
            'is_closed': 1
        },
        'Friday': {
            'day': 'Friday',
            'is_closed': 1
        },
        'Saturday': {
            'day': 'Saturday',
            'is_closed': 1
        }
    };

    hours.map(day => {
        $ordered_days[day.day] = day;
    });

    let $ordered_array = $ordered_days.map((value, index) => {
        return [value];
    });

    return $ordered_array;
};
